import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import BrandsModal from './BrandsModal';

const BrandsModalContext = createContext(null);

/**
 * Un solo modal de Identidad para toda la app. Si cada diálogo de compartir
 * montaba el suyo, al abrir "Usar mi marca" quedaban dos Dialog de MUI
 * anidados: el form se duplicaba (mismo `id`), el nativo "Completa este campo"
 * apuntaba a cualquier lado y los títulos se pintaban encima de los campos.
 */
export function BrandsModalProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [startOnCreate, setStartOnCreate] = useState(false);
  const closeListenersRef = useRef(new Set());

  const openBrands = useCallback((options = {}) => {
    setStartOnCreate(options.startOnCreate === true);
    setOpen(true);
  }, []);

  const closeBrands = useCallback(() => {
    setOpen(false);
    setStartOnCreate(false);
    closeListenersRef.current.forEach((listener) => listener());
  }, []);

  const subscribeBrandsClose = useCallback((listener) => {
    closeListenersRef.current.add(listener);
    return () => closeListenersRef.current.delete(listener);
  }, []);

  const value = useMemo(() => ({
    brandsOpen: open,
    openBrands,
    closeBrands,
    subscribeBrandsClose,
  }), [open, openBrands, closeBrands, subscribeBrandsClose]);

  return (
    <BrandsModalContext.Provider value={value}>
      {children}
      <BrandsModal
        open={open}
        startOnCreate={startOnCreate}
        onClose={closeBrands}
      />
    </BrandsModalContext.Provider>
  );
}

const NOOP_SUB = () => () => {};

export function useBrandsModal() {
  const ctx = useContext(BrandsModalContext);
  if (!ctx) {
    return {
      brandsOpen: false,
      openBrands: () => {},
      closeBrands: () => {},
      subscribeBrandsClose: NOOP_SUB,
    };
  }
  return ctx;
}

/**
 * Props para el Dialog de compartir: mientras Identidad está abierto el de
 * compartir sigue montado (no se pierde el estado) pero no pelea foco ni capa.
 */
export function useShareDialogBrandsOverlap() {
  const { brandsOpen } = useBrandsModal();
  return {
    hideBackdrop: brandsOpen,
    disableEnforceFocus: brandsOpen,
    disableAutoFocus: brandsOpen,
    sx: brandsOpen ? { visibility: 'hidden' } : undefined,
  };
}
